import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { InterviewOut, Score as ScoreRow } from '@/api/interviews';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { onDateTime } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

import { AddAddendum } from './AddendumDialog';
import { AddendumList } from './AddendumList';
import { OtherInterviews } from './OtherInterviews';
import { OverallVerdict } from './OverallVerdict';
import { QuestionRating } from './QuestionRating';

interface Props {
  /** A **submitted** interview this reader may read in full — `index.tsx`'s branch guarantees both. */
  interview: InterviewOut;
  /** The viewer, because every key on this screen carries them (ADR 0006). */
  viewerId: number;
}

/**
 * §6.7 — **submitted feedback as the record it is**: the same content as §6.6, read-only, plus what was
 * hidden before.
 *
 * ⚠️ **Feedback protection #5 — "submitted feedback is immutable" (ADR 0008) — and this screen pays the
 * cost the ADR books against itself.** There is **no edit button**, and in its place a sentence that says
 * why: *"Submitted 12 March, 14:32. Submitted feedback cannot be changed."* §6.7 states the reason for the
 * sentence existing at all, and it is not decoration — *"the absence of an edit button reads as a missing
 * feature unless the interface says why. Someone will file it as a bug; that sentence is what stops it,
 * and it is why the sentence is on the screen rather than in a tooltip."* Both `docs/adr/README.md` and
 * ADR 0008's own *Implementation status* recorded this cost as **UNPAID** from Sprint 4 until TASK-085,
 * and those records are corrected in the same pass.
 *
 * ⚠️ **Not a tooltip, not a disabled control, and not a hidden one.** A disabled *Edit* says the reader
 * lacks a permission; there is no permission anywhere that grants it — not the author's, not an
 * `Admin`'s, not a superuser's, and the Django admin is read-only on `Interview`, `Score` and `Addendum`.
 * The honest rendering is a sentence and, for the author, the one thing that *is* possible: an addendum
 * (TASK-086).
 *
 * ⚠️ **Feedback protection #1 — the snapshot (ADR 0004) — and this is the only screen in the product that
 * reads `question_has_been_edited`.** Sprint 10's TASK-074 found the field in the payload with no screen
 * reading it. §6.6 deliberately does not (*"the wording in front of them is the wording they must ask"* —
 * `QuestionBlock`); here it is the note that makes the invariant **visible** rather than merely correct:
 * the wording on screen is the wording that was answered, and the bank has moved on since.
 *
 * ⚠️ ~~**Nothing here can reach the live wording, and that is structural rather than disciplined.** The
 * only route carrying a question's current text is the bank, which is `Admin`-only — so `Recruiter`s and
 * `Interviewer`s could not fetch it if they tried, and this screen does not try:~~ ⚠️ **That premise is
 * retracted twice over and the conclusion turns the other way up: nothing here *does* reach the live
 * wording, and that is disciplined rather than structural.** ADR 0018 **reversed** the `Admin`-only row
 * (2026-08-11) and ADR 0019 deleted the route it was about (2026-09-24); a question's live wording is
 * reached through the set that owns it, and that read answers all three groups with its rows narrowed
 * per reader. A `Recruiter` or an `Interviewer` reading submitted feedback could therefore fetch it,
 * and this screen still does not — which makes the guard *more* load-bearing than it was, not less.
 *
 * ⚠️ **And the test now asserts the route this argument turns on** _(extended 2026-09-24, third review
 * round)_. `submitted.test.tsx` seeds the live wording on **both** addresses that carry it — the set
 * list and the set's own read, `GET /api/v1/question-sets/` and `…/{id}/` — and asserts that no state
 * of this screen reaches either, nor `/api/v1/roles/`. Until that run it named the catalogue alone,
 * so the paragraph above was arguing from an assertion nobody had written; `fillingIn.test.tsx` had
 * taken the same extension for §6.6, and the two screens now guard one absence the same way.
 */
export function Submitted({ interview, viewerId }: Props) {
  const { t, i18n } = useTranslation(['interviews', 'candidates']);

  /*
   * ⚠️ **This screen fetched the person card for two fields, and backend TASK-107 put both on the
   * interview payload** — the name, and `anonymized_at`, which on a submitted interview is the difference
   * between *"this comment is missing"* and *"this comment was erased on request"*. Half a fix would have
   * left the request in place for the second one, which is why they travel together.
   */
  const anonymizedAt = interview.consideration.person_anonymized_at;

  /**
   * ADR 0008: an addendum is the **author's** and nobody else's, and it amends something **submitted** —
   * so both conditions are required. §6.7's *Access* line says who may *see* this screen; it is not who
   * may write on it.
   */
  const mayAddAddendum =
    interview.interviewer_id === viewerId &&
    interview.submitted_at !== null &&
    /*
     * ⚠️ **An erased candidate closes the record to new activity (ADR 0009), so the control goes** — the
     * same rule §6.5 applies to *Edit* (`CardMenu`: `mayEdit = … && !anonymized`), and the API answers
     * this route with the same 409. Hidden rather than disabled, and hidden on the **payload's own**
     * `anonymized_at` rather than on a refusal, so nobody writes a paragraph they cannot save. The
     * dialog still answers that 409 in a sentence, because a card erased in another tab is not a state
     * this screen can rule out.
     */
    anonymizedAt === null;

  return (
    <>
      <header className="border-b border-rule pb-4 pt-4">
        <div className="min-w-0">
          {/* From this screen's own payload since backend TASK-107 — no second request, no second
              loading state. */}
          <p className={type.sectionHeading}>{interview.consideration.person_full_name}</p>
          <p className={cn(type.metadata, 'mt-1')}>{interview.consideration.role_title}</p>
        </div>

        {/*
         * ⚠️ **§6.7's line, and it renders for every reader of a submitted interview** — the author, a
         * `Recruiter` and an `Admin` alike. It is the *record's* property and not this reader's
         * permission, so a version of it that appeared only for the author would leave the other two
         * looking for the edit button §6.7 exists to explain the absence of.
         */}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          {/*
           * ⚠️ **Both halves are gated on `submitted_at`, and the case that makes that necessary is the
           * impossible one.** `index.tsx` answers a *readable draft belonging to somebody else* with this
           * screen — a payload a correct server never sends (ADR 0006 hides every other draft behind five
           * keys), kept as the safe answer to a backend regression, a proxy cache or a devtools replay.
           * On that payload there is no submission to date and nothing immutable yet, so neither sentence
           * may be said; what remains is a read-only page, which is the point of answering with it.
           */}
          {interview.submitted_at !== null && (
            <>
              <p className={cn(type.body, type.numeral)}>
                <time dateTime={interview.submitted_at}>
                  {t('interviews:submitted.on', {
                    date: onDateTime(interview.submitted_at, i18n.language),
                  })}
                </time>
              </p>
              <p className={cn(type.body, 'font-medium')}>{t('interviews:submitted.immutable')}</p>
            </>
          )}

          {/*
           * `[Add addendum]` — **the author's, and nobody else's** (§6.7, decided 2026-07-24; the API
           * answers 403 to everyone else). Hidden rather than disabled, which is §5's rule for a
           * capability a viewer does not have: a disabled control here would read as *"ask somebody for
           * the right"*, and there is no right to ask for.
           */}
          {mayAddAddendum && <AddAddendum interview={interview} viewerId={viewerId} />}
        </div>

        {/*
         * ⚠️ **The erasure is named, and this screen is where it earns its line** (ADR 0009, protection
         * #6). An anonymised candidate's comments are `''` while every rating, verdict and timestamp
         * stands — so without this sentence a reader meets an interview of empty comments and concludes
         * the product lost them, or that the interviewer wrote nothing. It is §6.5's own wording, so the
         * product says it once, and `DESIGN.md` §6.7 now carries the decision.
         */}
        {anonymizedAt !== null && (
          <p className={cn(type.body, 'mt-3 text-ink-700')}>
            {t('candidates:card.anonymizedOn', {
              date: onDateTime(anonymizedAt, i18n.language),
            })}
          </p>
        )}
      </header>

      {interview.scores.length === 0 ? (
        /* The same real state §6.6 renders: a vacancy whose bank was empty when this was scheduled. */
        <p className={cn(type.body, 'mt-8')}>{t('interviews:submitted.noQuestions')}</p>
      ) : (
        <>
          <Eyebrow className="mt-8" count={interview.scores.length}>
            {t('interviews:filling.questions')}
          </Eyebrow>
          {/* The payload's own order — the frozen bank order (ADR 0004). Nothing sorts or filters it. */}
          <ol className="divide-y divide-rule">
            {interview.scores.map((score) => (
              <SubmittedScore key={score.id} score={score} />
            ))}
          </ol>
        </>
      )}

      {/*
       * ⚠️ **The document's conclusion, and the last thing in the record** (§6.7, decided in TASK-118):
       * after every question, with the overall comment beneath it, at the size §6.2 gives a dashboard
       * metric. *Last in the record* is not *last on the page* — the addenda below still render
       * **below** it, because an addendum amends the whole write-up including this line (ADR 0008), and
       * *Other interviews* is navigation rather than record.
       */}
      <section className="mt-10">
        <Eyebrow>{t('interviews:filling.overall')}</Eyebrow>

        <div className="mt-3">
          {/*
           * The verdict as a **word and a colour** and never a colour alone (§3, §9). A submitted
           * interview always has one — the completeness gate requires it — but the type does not say so,
           * and inventing a fallback verdict would be worse than saying nothing.
           */}
          {interview.overall_recommendation !== null ? (
            <OverallVerdict value={interview.overall_recommendation} />
          ) : (
            <span className={cn(type.body, 'text-ink-500')}>
              {t('interviews:submitted.noVerdict')}
            </span>
          )}
        </div>

        {/*
         * The overall comment beneath it — the interviewer's own writing, so it carries the provenance
         * rule the per-question answers carry (§6.6's *"the answer told apart from the question"*).
         */}
        <Comment text={interview.overall_comment} className="mt-4 border-l border-rule pl-4" />
      </section>

      {/* The addenda, below the original and never merged into it (ADR 0008, §6.7). */}
      <AddendumList addenda={interview.addenda} viewerId={viewerId} />

      {/* What was hidden before: the candidate's other **submitted** interviews, as links. */}
      <OtherInterviews interview={interview} viewerId={viewerId} />
    </>
  );
}

/** One answered question: the wording as it was asked, the rating, the comment, and the note. */
function SubmittedScore({ score }: { score: ScoreRow }) {
  const { t } = useTranslation(['interviews', 'components']);

  /** §9's pair rule: the mark in the margin is described by the wording it is a mark against. */
  const ids = useId();
  const questionId = `${ids}-question`;

  return (
    <li className="py-6 first:pt-0">
      {/*
       * ⚠️ **The rating in the margin, beside the question it belongs to** (§6.7, decided in TASK-118).
       * Until 2026-08-11 it was a ~12px numeral right-aligned across `max-w-3xl` from its own question —
       * `flex items-start justify-between` — so reading *"what did they score"* cost eye travel across
       * the page and back, once per question, and §3's coloured rule under the numeral was invisible at
       * that size. An interview write-up **is** a marked-up reading; a scored margin is true to the
       * subject rather than applied to it (§1).
       *
       * ⚠️ **Flex and never a grid**, and that is load-bearing rather than a preference:
       * `fillingIn.test.tsx` walks this folder's source for a grid column class, because §10 asks for a
       * single column below `lg` and the guard against a second layout is that there is none to switch
       * off. This margin is one layout at every width — the mark is 48px wide and a question is readable
       * beside it on the narrowest width §10 designs for.
       */}
      <div className="flex gap-5">
        <div className="w-12 shrink-0 pt-4">
          <QuestionRating value={score.rating} describedBy={questionId} />
        </div>

        <div className="min-w-0 flex-1">
          {/*
            §6.6's eyebrow, on the read view too (backend TASK-105) — the same two live facts above the
            same frozen wording. §6.7 inherits §6.6's question list rather than describing its own, and a
            reader comparing a submitted interview with the one they are filling in should not find the
            rows shaped differently.
          */}
          <p className={type.eyebrow}>
            {t(`components:category.${score.question_category}`)} ·{' '}
            {t(`components:difficulty.${score.question_difficulty}`)}
          </p>

          {/*
           * `question_text_snapshot` and nothing else — the wording that was actually answered. It keeps
           * body treatment: **greying it was considered and rejected** (§6.7), because the question is
           * the frozen record and the anchor of the block, and `ink-500` would say it matters less.
           */}
          <p id={questionId} className="mt-1 text-[15px] leading-[1.6] text-ink-900">
            {score.question_text_snapshot}
          </p>

          {/*
           * ⚠️ **The note ADR 0004 exists to make visible, and it is a *note*** — `ink-500`, in reading
           * order beneath the question it is about, with **no `role="alert"`**. Nothing is wrong: the
           * record is intact and this is what "intact" looks like from the outside. An alarm here would
           * suggest the reader should distrust the wording above it, which is the opposite of the
           * invariant. It stays with the **question**, outside the rule below, because it is the product
           * speaking about the record rather than anything the interviewer wrote.
           */}
          {score.question_has_been_edited && (
            <p className={cn(type.metadata, 'mt-2')}>{t('submitted.edited')}</p>
          )}

          {/*
           * ⚠️ **The provenance device** (§6.6, §6.7): the interviewer's own writing sits behind a
           * hairline with a small indent, the frozen wording above does not. Rules, not colour and not a
           * box — §1's own vocabulary, and the difference it encodes is a fact about the content (ADR
           * 0004's snapshot) rather than a decoration. It says nothing about the question's history: the
           * note above is what carries that, here and on no other screen.
           */}
          <Comment text={score.comment} className="mt-3 border-l border-rule pl-4" />
        </div>
      </div>
    </li>
  );
}

/**
 * A comment at §3's *Feedback comment* spec — 15px, `leading-[1.7]`, **`max-w-[68ch]`**, because §3 says
 * in as many words: *"Never render a comment full-bleed across a wide monitor."*
 *
 * An empty comment renders **nothing at all** rather than an empty block or a dash: on this screen an
 * absent comment is either a question answered without one (lawful — only 1, 2 and 5 require one) or a
 * comment erased on request (ADR 0009), and the erasure line in the header is what names the second case.
 * A placeholder would claim the product had something to show and lost it.
 */
function Comment({ text, className }: { text: string; className?: string }) {
  if (text.trim() === '') return null;

  return <p className={cn(type.feedbackComment, 'whitespace-pre-line', className)}>{text}</p>;
}
